import React, { useState, useEffect } from 'react';
import { AutoMapSuggestion, autoMapURLs, saveURLMappings, listDocuments } from '../../services/api';
import './AutoMapModal.css';

interface AutoMapModalProps {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface MappingRow extends AutoMapSuggestion {
  selectedFile: string;
}

const AutoMapModal: React.FC<AutoMapModalProps> = ({ projectId, isOpen, onClose, onSaved }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mappings, setMappings] = useState<MappingRow[]>([]);
  const [unmappedDocs, setUnmappedDocs] = useState<string[]>([]);
  const [allDocuments, setAllDocuments] = useState<string[]>([]);
  const [hasUrlCsv, setHasUrlCsv] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const loadAutoMapData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [autoMapResult, docsResult] = await Promise.all([
        autoMapURLs(projectId),
        listDocuments(projectId)
      ]);

      setHasUrlCsv(autoMapResult.has_url_csv);
      setUnmappedDocs(autoMapResult.unmapped_documents);
      setAllDocuments(docsResult.documents.map(d => d.filename));

      if (autoMapResult.message) {
        setMessage(autoMapResult.message);
      }

      // Convert suggestions to editable rows
      const rows: MappingRow[] = autoMapResult.suggestions.map(s => ({
        ...s,
        selectedFile: s.matched_file || ''
      }));
      setMappings(rows);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load auto-mapping data');
    } finally {
      setIsLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen) {
      loadAutoMapData();
    }
  }, [isOpen, projectId]);

  const handleFileChange = (index: number, filename: string) => {
    const updated = [...mappings];
    updated[index].selectedFile = filename;
    updated[index].auto_mapped = false; // Now it's manually mapped
    setMappings(updated);
  };

  const getAvailableFiles = (currentSelected: string) => {
    const usedFiles = new Set(mappings.map(m => m.selectedFile).filter(f => f && f !== currentSelected));
    return allDocuments.filter(f => !usedFiles.has(f));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    try {
      const validMappings = mappings
        .filter(m => m.selectedFile && m.public_url)
        .map(m => ({
          filename: m.selectedFile,
          display_name: m.csv_name,
          public_url: m.public_url
        }));

      await saveURLMappings(projectId, validMappings);
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save mappings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  const autoMappedCount = mappings.filter(m => m.auto_mapped && m.selectedFile).length;
  const needsManualCount = mappings.filter(m => !m.selectedFile).length;
  const alreadySavedCount = mappings.filter(m => m.already_saved).length;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="auto-map-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>🔗 Auto-Map URLs to Documents</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {isLoading ? (
            <div className="loading">⏳ Analyzing documents and URL mappings...</div>
          ) : !hasUrlCsv ? (
            <div className="no-csv-message">
              <p>📁 {message || 'No urls.csv file found.'}</p>
              <p>Please upload a URL mapping CSV in the URL Mappings section first.</p>
            </div>
          ) : (
            <>
              {error && <div className="error-message">⚠️ {error}</div>}

              <div className="stats-bar">
                <span className="stat auto">✓ Auto-mapped: {autoMappedCount}</span>
                <span className="stat manual">⚠️ Needs mapping: {needsManualCount}</span>
                <span className="stat saved">💾 Already saved: {alreadySavedCount}</span>
              </div>

              {mappings.length > 0 && (
                <div className="mappings-table-container">
                  <table className="mappings-table">
                    <thead>
                      <tr>
                        <th>URL CSV Name</th>
                        <th>Matched Document</th>
                        <th>Confidence</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mappings.map((m, idx) => (
                        <tr key={idx} className={m.auto_mapped ? 'auto-mapped' : m.selectedFile ? 'manual-mapped' : 'unmatched'}>
                          <td>
                            <div className="csv-name">{m.csv_name}</div>
                            <a href={m.public_url} target="_blank" rel="noopener noreferrer" className="url-link">
                              🔗 {m.public_url.length > 40 ? m.public_url.substring(0, 40) + '...' : m.public_url}
                            </a>
                          </td>
                          <td>
                            <select
                              value={m.selectedFile}
                              onChange={(e) => handleFileChange(idx, e.target.value)}
                              className="file-select"
                            >
                              <option value="">-- Select document --</option>
                              {getAvailableFiles(m.selectedFile).map(f => (
                                <option key={f} value={f}>{f}</option>
                              ))}
                            </select>
                          </td>
                          <td className="confidence">
                            {m.confidence > 0 ? `${Math.round(m.confidence * 100)}%` : '-'}
                          </td>
                          <td>
                            {m.already_saved ? (
                              <span className="status saved">💾 Saved</span>
                            ) : m.auto_mapped ? (
                              <span className="status auto">✓ Auto</span>
                            ) : m.selectedFile ? (
                              <span className="status manual">✓ Manual</span>
                            ) : (
                              <span className="status pending">⚠️ Unmatched</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {unmappedDocs.length > 0 && (
                <div className="unmapped-docs">
                  <h4>📄 Documents without URL mapping ({unmappedDocs.length})</h4>
                  <ul>
                    {unmappedDocs.slice(0, 10).map(d => <li key={d}>{d}</li>)}
                    {unmappedDocs.length > 10 && <li className="more">...and {unmappedDocs.length - 10} more</li>}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>Cancel</button>
          <button
            className="save-btn"
            onClick={handleSave}
            disabled={isSaving || isLoading || !hasUrlCsv || mappings.filter(m => m.selectedFile).length === 0}
          >
            {isSaving ? '⏳ Saving...' : '💾 Save Mappings'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoMapModal;

